import { useEffect, useState } from 'react';

import { buildNavPath as buildNavPathHelper } from 'helpers/candidate_application/form-page-helpers';
import { useQueryParams } from 'hooks/query-params';
import CandidatePage from 'layouts/CandidatePage/CandidatePage';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from 'components/FormComponents';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';

import {
  createDrugScreenOrder,
  getI3DrugScreenSites,
  scheduleDrugScreen,
  selectCandidateApplication,
} from 'api/candidate-application-slice';

import styles from './CandidateUsDrugScreen.module.scss';

export const CandidateUsDrugScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [admin] = useQueryParams(['admin']);
  const { token } = useParams();

  const buildNavPath = path => buildNavPathHelper(path, admin);

  const {
    application: { candidate_checks, brand, check_values },
    fetched,
  } = useSelector(selectCandidateApplication);

  const [check, setCheck] = useState(null);
  const [hasOrder, setHasOrder] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [postalCode, setPostalCode] = useState('');

  const [pendingSubChecks, setPendingSubChecks] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [loadingSites, setLoadingSites] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduled, setScheduled] = useState(false);
  const [sitesError, setSitesError] = useState(null);
  const [scheduleError, setScheduleError] = useState(null);

  useEffect(() => {
    if (!fetched) return;
    const found = candidate_checks.find(c => c.type === 'us_medical_screen');
    setCheck(found);
    if (
      found?.submitted_at ||
      check_values.system_values?.us_medical_screen_status === 'completed'
    ) {
      setScheduled(true);
    } else if (found?.sent_to_provider) {
      setHasOrder(true);
      const pending = (found.sub_checks || []).filter(sc => !sc.submitted_at && !sc.canceled);
      if (pending.length === 0) {
        setScheduled(true);
      } else {
        setPendingSubChecks(pending);
      }
    }
    setPostalCode(check_values.values?.residential_address?.postal_code ?? '');
  }, [fetched]);

  const createOrder = async () => {
    setCreatingOrder(true);
    setOrderError(null);
    const result = await dispatch(createDrugScreenOrder({ token, id: check.id }));
    setCreatingOrder(false);
    if (result.error) {
      setOrderError(t('candidatePortal.medical.orderError'));
      return;
    }
    const pending = (result.payload?.sub_checks || []).filter(
      sc => !sc.submitted_at && !sc.canceled,
    );
    setHasOrder(true);
    setPendingSubChecks(pending);
  };

  useEffect(() => {
    if (!check || hasOrder) return;
    createOrder();
  }, [check, hasOrder]);

  const currentSubCheck = pendingSubChecks[currentStepIndex];

  const searchSites = async () => {
    if (!currentSubCheck) return;
    setSitesError(null);
    setSites([]);
    setSelectedSite(null);
    setLoadingSites(true);
    const result = await dispatch(
      getI3DrugScreenSites({
        token,
        id: check.id,
        postal_code: postalCode,
        sub_check_id: currentSubCheck.id,
      }),
    );
    setLoadingSites(false);
    if (result.error || !result.payload?.sites) {
      setSitesError(t('candidatePortal.medical.sitesError'));
      return;
    }
    const fetchedSites = result.payload.sites;
    if (fetchedSites.length === 0) {
      setSitesError(t('candidatePortal.medical.noSitesFound'));
      return;
    }
    setSites(fetchedSites);
  };

  useEffect(() => {
    if (!hasOrder || !postalCode || !currentSubCheck) return;
    searchSites();
  }, [hasOrder, postalCode, currentStepIndex]);

  const confirmSchedule = async () => {
    setScheduleError(null);
    setScheduling(true);
    const result = await dispatch(
      scheduleDrugScreen({
        token,
        id: check.id,
        sub_check_id: currentSubCheck.id,
        collection_site_link_id: selectedSite.collection_site_link_id,
        site_name: selectedSite.collection_site_name,
        site_address: selectedSite.collection_site_address,
        site_city: selectedSite.collection_site_city,
        site_state: selectedSite.collection_site_state,
        site_phone: selectedSite.collection_site_phone,
      }),
    );
    setScheduling(false);
    if (result.error || !result.payload?.scheduled) {
      setScheduleError(t('candidatePortal.medical.scheduleError'));
      return;
    }
    if (result.payload.all_scheduled) {
      setScheduled(true);
    } else {
      setCurrentStepIndex(prev => prev + 1);
      setSites([]);
      setSelectedSite(null);
      setSitesError(null);
    }
  };

  const stepLabel =
    pendingSubChecks.length > 1 && currentSubCheck
      ? t('candidatePortal.medical.testStep', {
          current: currentStepIndex + 1,
          total: pendingSubChecks.length,
          title: currentSubCheck.title,
        })
      : null;

  return (
    <CandidatePage token={token} brand={brand} loading={creatingOrder || scheduling}>
      <CandidatePage.View>
        <CandidatePage.Card>
          {!fetched && <p>{t('common.loading')}</p>}

          {fetched && !hasOrder && (
            <CreatingOrder orderError={orderError} onRetry={createOrder} t={t} />
          )}

          {fetched && hasOrder && scheduled && (
            <Scheduled
              onDashboard={() =>
                navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`))
              }
              t={t}
            />
          )}

          {fetched && hasOrder && !scheduled && !selectedSite && (
            <SiteSearch
              stepLabel={stepLabel}
              loadingSites={loadingSites}
              sitesError={sitesError}
              sites={sites}
              onRetry={searchSites}
              onSelectSite={setSelectedSite}
              t={t}
            />
          )}

          {fetched && hasOrder && !scheduled && selectedSite && (
            <SiteConfirm
              stepLabel={stepLabel}
              selectedSite={selectedSite}
              scheduleError={scheduleError}
              scheduling={scheduling}
              onBack={() => setSelectedSite(null)}
              onConfirm={confirmSchedule}
              t={t}
            />
          )}
        </CandidatePage.Card>
      </CandidatePage.View>
    </CandidatePage>
  );
};

const formatSiteAddress = site =>
  [
    [site.collection_site_address, site.collection_site_address2].filter(Boolean).join(', '),
    [
      [site.collection_site_city, site.collection_site_state].filter(Boolean).join(', '),
      site.collection_site_zip,
    ]
      .filter(Boolean)
      .join(' '),
  ]
    .filter(Boolean)
    .join(', ');

const SiteMeta = ({ site, t }) => (
  <div className={styles.siteCardRight}>
    {site.distance_miles_numeric != null && (
      <span className={styles.siteDistance}>
        {t('candidatePortal.medical.distanceMi', { distance: site.distance_miles_numeric })}
      </span>
    )}
    <div className={styles.siteMeta}>
      {site.collection_site_phone && (
        <div className={styles.siteMetaRow}>
          <b className={styles.siteMetaLabel}>{t('candidatePortal.medical.phone')}:</b>
          <span className={styles.siteMetaValue}>{site.collection_site_phone}</span>
        </div>
      )}
    </div>
  </div>
);

const CreatingOrder = ({ orderError, onRetry, t }) => (
  <>
    {orderError ? (
      <>
        <FormMessage message={orderError} type={MessageType.Warning} closeable={false} noMargin />
        <Button type="primary" onClick={onRetry}>
          {t('common.tryAgain')}
        </Button>
      </>
    ) : (
      <p>{t('candidatePortal.medical.preparingOrder')}</p>
    )}
  </>
);

const Scheduled = ({ onDashboard, t }) => (
  <>
    <FormMessage
      message={t('candidatePortal.medical.scheduledMessage')}
      type={MessageType.Success}
      closeable={false}
      noMargin
    />
    <Button type="primary" onClick={onDashboard} className="u-margin-top--large">
      {t('candidatePortal.medical.backToDashboard')}
    </Button>
  </>
);

const SiteSearch = ({ stepLabel, loadingSites, sitesError, sites, onRetry, onSelectSite, t }) => (
  <div className={styles.search}>
    {stepLabel && <p className={styles.stepLabel}>{stepLabel}</p>}

    {loadingSites && <p>{t('candidatePortal.medical.searchingSites')}</p>}

    {sitesError && (
      <>
        <FormMessage message={sitesError} type={MessageType.Warning} closeable={false} noMargin />
        <Button type="primary" onClick={onRetry}>
          {t('common.tryAgain')}
        </Button>
      </>
    )}

    {sites.length > 0 && (
      <div className={styles.siteList}>
        {sites.map(site => (
          <button
            key={site.collection_site_link_id}
            className={styles.siteCard}
            onClick={() => onSelectSite(site)}>
            <div className={styles.siteCardLeft}>
              <span className={styles.siteName}>{site.collection_site_name}</span>
              <span className={styles.siteAddress}>{formatSiteAddress(site)}</span>
              {site.today_hours && (
                <span className={styles.siteHours}>
                  {t('candidatePortal.medical.todayHours', { hours: site.today_hours })}
                </span>
              )}
            </div>
            <SiteMeta site={site} t={t} />
          </button>
        ))}
      </div>
    )}
  </div>
);

const SiteConfirm = ({
  stepLabel,
  selectedSite,
  scheduleError,
  scheduling,
  onBack,
  onConfirm,
  t,
}) => (
  <div className={styles.confirm}>
    {stepLabel && <p className={styles.stepLabel}>{stepLabel}</p>}
    <h3 className={styles.confirmTitle}>{t('candidatePortal.medical.confirmTitle')}</h3>
    <div className={styles.confirmSiteCard}>
      <div className={styles.siteCardLeft}>
        <span className={styles.siteName}>{selectedSite.collection_site_name}</span>
        <span className={styles.siteAddress}>{formatSiteAddress(selectedSite)}</span>
        {selectedSite.today_hours && (
          <span className={styles.siteHours}>
            {t('candidatePortal.medical.todayHours', { hours: selectedSite.today_hours })}
          </span>
        )}
      </div>
      <SiteMeta site={selectedSite} t={t} />
    </div>

    {scheduleError && (
      <FormMessage message={scheduleError} type={MessageType.Warning} closeable={false} noMargin />
    )}

    <div className={styles.actions}>
      <Button type="secondary" onClick={onBack}>
        {t('common.back')}
      </Button>
      <Button type="primary" loading={scheduling} onClick={onConfirm}>
        {t('candidatePortal.medical.confirmSchedule')}
      </Button>
    </div>
  </div>
);

export default CandidateUsDrugScreen;
